import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  arrow,
  useFloatingNodeId,
  size,
  Strategy,
} from '@floating-ui/react';
import { useMemo, useRef, useState } from 'react';
import { PopoverPlacement } from '../..';

type UsePopoverConfig = {
  placement: PopoverPlacement;
  forcePlacement?: boolean;
  controlledIsOpen?: boolean;
  closeOnBlur: boolean;
  closeOnEscape?: boolean;
  onClose?: () => any;
  strategy: Strategy;
};

export function usePopover({
  placement,
  forcePlacement = false,
  closeOnBlur,
  controlledIsOpen,
  closeOnEscape = true, // provides accessibility for closing popover with keyboard by default
  strategy,
  onClose,
}: UsePopoverConfig) {
  // Subscribe this component to the <FloatingTree> wrapper:
  const nodeId = useFloatingNodeId();

  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(controlledIsOpen);
  const open = controlledIsOpen ?? isOpen;
  const blurEventsToPrevent = ['focusin', 'pointerdown'];

  const data = useFloating({
    nodeId,
    placement,
    open,
    strategy,
    onOpenChange: (isOpen, event) => {
      // Leave popover open if user focuses out or clicks elsewhere on the page
      if (!closeOnBlur && blurEventsToPrevent.includes(event?.type || '')) {
        return;
      }

      setIsOpen(isOpen);

      if (onClose && !isOpen) {
        onClose();
      }
    },
    // ensure the floating element remains anchored to the reference element when scrolling and resizing the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      // Distance popover content is from trigger
      offset(10),
      // Only use flip middleware when forcePlacement is false
      !forcePlacement &&
        flip({
          // Place popover content below if there is no room left/right/top
          fallbackAxisSideDirection: 'end',
          padding: 5,
        }),
      // Stops popover content from hitting edge of screen, instead leave a small gap
      shift({ padding: 5 }),
      // Needs to after shift, otherwise arrow won't appear correctly
      arrow({
        element: arrowRef,
      }),
      size({
        apply({ elements, rects, availableHeight, availableWidth }) {
          const { width: popoverWidth } = rects.floating;
          const offset = 10; // avoid floating element hitting edge of browser

          const shouldLimitWidth = popoverWidth > availableWidth - offset;

          Object.assign(elements.floating.style, {
            // Ensure menu not wider than height of viewport
            maxWidth: shouldLimitWidth ? `${availableWidth - offset}px` : '',
            maxHeight: `${availableHeight - offset}px`,
          });
        },
      }),
    ],
  });

  const context = data.context;
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    escapeKey: closeOnEscape,
    outsidePress: !controlledIsOpen,
  });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return useMemo(
    () => ({
      open,
      isOpen,
      setIsOpen,
      nodeId,
      arrowRef,
      ...interactions,
      ...data,
    }),
    [open, setIsOpen, interactions, data]
  );
}
